// Globális IDŐVÁLASZTÓ (Popover + rácsos lista) — drop-in a natív `<input type="time">` helyére, a VÁLTOZATLAN
// `HH:mm` string szerződéssel (üres = nincs kiválasztva).
//
// MIÉRT LÉTEZIK: a `DateTimePicker` dátum-fele a design-rendszer szerinti `DatePicker` volt, az idő-fele viszont
// natív `type="time"` — és a natív widget megjelenése BÖNGÉSZŐ-SPECIFIKUS (Chrome-on óra-ikonos spinner,
// Safari-n más), tehát elütött a mellette álló, stylizált dátum-gombtól. Ez volt az EGYETLEN natív form-widget,
// ami a rendszerben maradt; a user „csúnya natív bizbasz"-ként jelentette. A `DatePicker`
// felépítését tükrözi (ugyanaz a trigger-markup, ugyanaz a „×" törlés) → a kettő egymás mellett EGY mezőnek látszik.
//
// A RÁCS: a lista `step` percenként generálódik (alap 15 perc = 96 elem). A rács NEM zárja ki a pontos időt:
// a `HH:mm` érték bármi lehet (pl. egy meglévő 10:07-es meeting), és a lista akkor a legközelebbi rács-elemet
// jelöli aktívnak, az értéket viszont VÁLTOZATLANUL mutatja — így egy szerkesztés nem kerekíti el az adatot.
import { ClockIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface TimePickerProps {
  /** `HH:mm` vagy üres string. */
  value: string;
  onChange: (value: string) => void;
  /** Placeholder + aria (pl. „Kezdés — idő"). */
  label?: string | undefined;
  /** A látható üres-szöveg, ha a mezőnek van külön címkéje (form): formátum-hint, NEM a címke ismétlése. */
  placeholder?: string | undefined;
  /** A rács lépése percben. Alap: 15 (→ 96 elem). */
  step?: number;
  /**
   * A választható ABLAK alsó/felső határa (`HH:mm`) — a RÁCS szűkítése, nem validáció. Alap: nincs (teljes nap).
   * MIÉRT rács-szűkítés: ha a domén ismeri a saját idő-ablakát (pl. a megbeszélések 06:00–22:00-ig tarthatók),
   * akkor a lista se ajánljon fel hajnali időpontot — a mentés-validáció külön, a hívó sémájában él.
   * A MEGLÉVŐ értéket SOHA nem írja át: egy ablakon kívüli, már mentett időpont változatlanul látszik
   * (a rács csak nem jelöli aktívnak) — a szerkesztés nem kerekít és nem töröl adatot.
   */
  minTime?: string | undefined;
  maxTime?: string | undefined;
  /** Zárolt mező — nem nyitható, nem törölhető. */
  disabled?: boolean;
  /** Törölhető-e a kiválasztás („×"). Alap: true. */
  clearable?: boolean;
}

/** `HH:mm` → perc éjféltől, vagy `null` ha nem értelmezhető. */
function toMinutes(value: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

/** perc éjféltől → `HH:mm` (mindig 2-2 jegy). */
function toLabel(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function TimePicker({
  value,
  onChange,
  label,
  placeholder,
  step = 15,
  minTime,
  maxTime,
  disabled = false,
  clearable = true,
}: TimePickerProps): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const current = toMinutes(value);

  const slots = useMemo(() => {
    const safeStep = step > 0 && step <= 720 ? step : 15;
    const from = toMinutes(minTime ?? '') ?? 0;
    const to = toMinutes(maxTime ?? '') ?? 24 * 60 - 1;
    const out: number[] = [];
    for (let m = from; m <= Math.min(to, 24 * 60 - 1); m += safeStep) out.push(m);
    // Üres rács (elrontott határok) → essünk vissza a teljes napra: egy választhatatlan mező rosszabb, mint egy
    // túl bő lista.
    if (out.length === 0) for (let m = 0; m < 24 * 60; m += safeStep) out.push(m);
    return out;
  }, [step, minTime, maxTime]);

  /** A rács azon eleme, ami a jelenlegi értékhez a LEGKÖZELEBB van (a kijelölés + a scroll-pozíció alapja). */
  const nearest = useMemo(() => {
    if (current === null) return null;
    return slots.reduce(
      (best, m) => (Math.abs(m - current) < Math.abs(best - current) ? m : best),
      slots[0]!,
    );
  }, [current, slots]);

  // Megnyitáskor a kiválasztott (vagy a hozzá legközelebbi) elemre görgetünk — 96 elemnél kézi keresés lenne.
  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => {
      listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'center' });
    });
    return () => cancelAnimationFrame(id);
  }, [open]);

  const showClear = current !== null && !disabled && clearable;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {/* A törlés-gomb a triggerrel TESTVÉR, nem beágyazott: `<button>` a `<button>`-ben érvénytelen HTML.
          (A `DatePicker` eredetileg egy `role="button"`-os SVG-t tett a triggerbe — de a Heroicons
          `aria-hidden="true"`-t is ad, tehát az a „gomb" sem volt a a11y-fában, sem Tabbal nem érhető el.) */}
      <div className="relative w-full">
        <PopoverTrigger asChild>
          {/* A trigger markupja SZÁNDÉKOSAN azonos a `DatePicker`-ével — a két mező egymás mellett egy egységnek
              látszik (a `DateTimePicker` így nem „gomb + natív widget", hanem két egyforma gomb). */}
          <button
            type="button"
            aria-label={label}
            disabled={disabled}
            className={cn(
              'border-border input-glass ease-atlas text-md flex h-8 w-full items-center gap-2 rounded-lg border px-2.5 py-1 transition-[border-color,box-shadow] duration-150',
              'focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
              showClear && 'pr-8',
            )}
          >
            <ClockIcon className="text-muted-foreground size-4 shrink-0" />
            <span
              className={cn(
                'min-w-0 flex-1 truncate text-left tabular-nums',
                current === null && 'text-subtle',
              )}
            >
              {/* A NYERS értéket mutatjuk (nem a rács-elemet): egy meglévő 10:07 nem kerekedik el a megnyitástól. */}
              {current !== null ? value : (placeholder ?? label ?? '')}
            </span>
          </button>
        </PopoverTrigger>
        {showClear && (
          <button
            type="button"
            aria-label={t('common.clear')}
            onClick={() => onChange('')}
            className="text-muted-foreground hover:text-foreground focus-visible:ring-accent-600 absolute right-1.5 top-1/2 flex size-5 -translate-y-1/2 items-center justify-center rounded focus-visible:outline-none focus-visible:ring-2"
          >
            <XMarkIcon className="size-4" />
          </button>
        )}
      </div>
      {/* portal={false}: modális Sheet/Dialog belsejében az idő-RÁCS görgethető maradjon. A body-szintű portál
          a Dialog scroll-lockja alá esik, ami a wheel-eseményt elnyeli (mérve az üzenet-időzítés dialógusán:
          96 elemű lista, és a görgetés semmit nem mozdított). Ugyanez a megoldás az `EntityCombobox`-ban. */}
      <PopoverContent align="start" portal={false} className="w-auto p-1">
        <div
          ref={listRef}
          role="listbox"
          aria-label={label ?? t('common.time')}
          className="max-h-64 w-28 overflow-y-auto"
        >
          {slots.map((m) => {
            const text = toLabel(m);
            const active = nearest === m;
            return (
              <button
                key={m}
                type="button"
                role="option"
                aria-selected={active}
                data-active={active}
                onClick={() => {
                  onChange(text);
                  setOpen(false);
                }}
                className={cn(
                  'text-md hover:bg-accent w-full rounded-md px-2 py-1 text-left tabular-nums transition-colors',
                  active && 'bg-accent text-foreground font-medium',
                )}
              >
                {text}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
